import { Avatar as ChakraAvatar, forwardRef, Tooltip } from "@chakra-ui/react";
import type { AvatarProps } from "@components/ui-kit/avatar/types";
import { avatarTextColor } from "@components/ui-kit/avatar/utils";
import { clickOnEnterOrSpace } from "@src/utils/events";
import { useMemo } from "react";

export const Avatar = forwardRef<AvatarProps, "span">(function Avatar(
  {
    src,
    colorScheme,
    tooltip,
    isTooltipDisabled,
    isHighlighted,
    isDisabled,
    onClick,
    ...props
  },
  ref,
) {
  // Contrast needs one color; a responsive value keeps the white text
  const textColor = useMemo(
    () => avatarTextColor(typeof colorScheme === "string" ? colorScheme : null),
    [colorScheme],
  );

  const isClickable = !!onClick && !isDisabled;

  const avatar = (
    <ChakraAvatar
      ref={ref}
      // White behind images keeps transparent ones readable
      bg={src ? "white" : (colorScheme ?? "teal.500")}
      color={textColor}
      src={src ?? undefined}
      borderWidth={isHighlighted ? "2px" : undefined}
      borderColor={isHighlighted ? "violet.500" : undefined}
      cursor={isClickable ? "pointer" : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={
        isClickable
          ? (event) => {
              // A clickable avatar often sits on a clickable card
              event.stopPropagation();
              onClick(event);
            }
          : undefined
      }
      onKeyDown={isClickable ? clickOnEnterOrSpace : undefined}
      {...props}
    />
  );

  if (!tooltip) return avatar;

  return (
    <Tooltip label={tooltip} placement="top" isDisabled={isTooltipDisabled}>
      {avatar}
    </Tooltip>
  );
});
